import Spec from "csi/core/Spec";
import csi from "csi/main";
import { DndItemWithId } from "designSystem/dnd/index";
import { action, computed, makeObservable, observable } from "mobx";
import { SyntheticEvent } from "react";
import { ICustomViewAttribute, ICustomViewAttributeGroup } from "src/c8-platform/type/customView";
import { C8AttributeKey } from "src/c8-platform/type/value";
import CustomViewAttribute from "./CustomViewAttribute";
import CustomViewAttributesStore from "./CustomViewAttributesStore";
import { compact, transform } from "lodash";

class CustomViewAttributeGroup implements DndItemWithId {
	/** attributes in group */
	readonly attributes = observable.array<CustomViewAttribute>([], { deep: false });

	/**
	 * Matrix, ad-hoc, and system groups will all have borders.
	 * Other groups are groups only in code, they are meant to look as if they are not in a group.
	 */
	get flat(): boolean {
		return !this.name && this.store.editingGroup?.id !== this.id;
	};

	/** unique id for group */
	readonly id: string;

	/** name of group */
	@observable name?: string;

	/** indicates if group is selected */
	selected = false;

	/** spec for group (whether it is an attribute or matrix group) */
	readonly spec?: Spec;

	/** attributes store */
	readonly store: CustomViewAttributesStore;

	/** return object that maps the attribute key by its index in the group */
	get attributeIndexMap(): Record<C8AttributeKey, number> {
		return csi.hashArray(this.attributes, (a: CustomViewAttribute) => a.id, Number);
	}

	/** return persistent object(s) to represent group's attribute keys */
	@computed get attributeKeys(): ICustomViewAttribute[] {
		const { attributes, groupAttributeParams } = this;
		const keys = attributes.map(({ id }) => id);

		if (!groupAttributeParams || !keys.length) {
			return keys;
		}
		
		return [{
			keys,
			...groupAttributeParams,
		}];
	}

	/** get ids of attributes in group */
	get attributeIds(): C8AttributeKey[] {
		return this.attributes.map(({ id }) => id);
	}

	/** indicates if group can be added to the view (selected) */
	get canAddGroup(): boolean {
		const { isAttributeGroup, store, unselectedAttributes } = this;
		const n = unselectedAttributes.length;
		return !!n && isAttributeGroup && store.canAddNumberOfAttributes(n);
	}

	/** indicates if group name can be edited */
	get canEditName(): boolean {
		return !this.spec;
	}

	/** object representing group in custom view, but with the child keys */
	get groupAttributeParams(): Omit<ICustomViewAttributeGroup, "keys"> {
		const { flat, id, isAttributeGroup, isMatrixGroup, name, spec } = this;

		if (flat) {
			return null;
		}

		if (isAttributeGroup) {
			return {
				groupKey: id,
				name,
			};
		}
		
		if (isMatrixGroup) {
			return {
				groupKey: id,
				matrixTarget: spec.matrixTargetClass.type,
				name,
			};
		}

		return {
			name,
		};
	}

	/** return persistent object(s) to represent group's hidden keys */
	@computed get hiddenKeys(): ICustomViewAttribute[] {
		const { attributes, groupAttributeParams } = this;
		
		let keys = transform(attributes, (result, { hidden, id }) => {
			if (hidden) {
				result.push(id);
			}
		}, []);

		// if group is flat (or has nothing hidden), then return
		if (!groupAttributeParams || !keys.length) {
			return keys;
		}
		
		// if entire group is hidden, then just store empty array
		if (keys.length === attributes.length) {
			keys = [];
		}

		return [{
			keys, 
			...groupAttributeParams,
		}];
	}

	/** indicate an adhoc (custom) group */
	get isAdhocGroup(): boolean {
		return !this.flat && !this.isAttributeGroup && !this.isMatrixGroup;
	}

	/** indicates if this group represents an attribute group */
	get isAttributeGroup(): boolean {
		return this.spec?.isMatrix === false;
	}

	/** indicates if the group contains no attributes */
	get isEmpty(): boolean {
		return !this.attributes.length;
	}

	/** indicates if this group is the last group in the selection */
	get isLastGroup(): boolean {
		return this.store.selections.length === 1;
	}

	/** indicates if this group represents a matrix group */
	get isMatrixGroup(): boolean {
		return !!this.spec?.isMatrix;
	}

	/** indicates if this group represents a metadata group */
	get isMetadataGroup(): boolean {
		return this.id.includes("___Metadata");
	}

	get isImageLabelGroup(): boolean {
		return this.id.includes("___Images");
	}

	/** list of visible matrix filter keys */
	@computed get matrixFilterKeys(): C8AttributeKey[] {
		const { attributes, isMatrixGroup, spec } = this;
		
		if (isMatrixGroup) {
			return [spec.matrixFilterKey];
		}

		const filterKeys = attributes.map(({ spec }) => spec.matrixFilterKey);
		return compact(filterKeys);
	}

	/** unique id */
	get uniqueId(): string {
		return this.spec?.uniqueId || this.id;
	}

	/** all unselected attributes in group */
	get unselectedAttributes(): CustomViewAttribute[] {
		return this.attributes.filter(({ selected }) => !selected);
	}

	/** return group with filtered set of attributes based on given regular expression if there are any matches */
	createFilteredGroup = (regex: RegExp): CustomViewAttributeGroup | undefined => {
		const { attributes, id, name, selected, spec, store } = this;
		const filteredAttributes = attributes.filter(({ spec }) => regex.test(spec.name));
		if (filteredAttributes.length) {
			return new CustomViewAttributeGroup(store, id, filteredAttributes, name, selected, spec);
		}
	};

	/** return the index of the attribute within the group */
	indexOfAttribute(attribute: CustomViewAttribute): number {
		return this.attributeIds.indexOf(attribute.id);
	}

	/** on click adhoc group title */
	onClickAdhocGroupTitle = (e: SyntheticEvent): void => {
		this.editAdhocGroup();
		const range = document.createRange();
		// Select the entire text within the element
		range.selectNodeContents(e.currentTarget);
		// Create a Selection object and add the Range to it
		const selection = window.getSelection();
		selection.removeAllRanges(); // Clear any existing selections
		selection.addRange(range);
	};

	/** add attribute to group */
	@action addAttribute = (attribute: CustomViewAttribute, indexMap?: Record<C8AttributeKey, number>): CustomViewAttribute => {
		const { attributes, isAttributeGroup, selected, store: { selectionSet, selectionSetWithParent, supportsGrouping } } = this;

		// copy attribute if not currently selected and adding to selected group (comes from available list);
		// the copied attribute will automatically be set as selected
		if (!attribute.selected && selected) {
			attribute = attribute.copy();
			attribute.selected = true;
			attribute.scheduledForScrollIntoView = true;
		} else if (!selected && selectionSet.has(attribute.id)) {
			// select the attribute if in selection set already and parent groups match
			attribute.selected = !supportsGrouping ||
				(!isAttributeGroup || selectionSetWithParent.has(attribute.createIdWithParent(this)));
		}

		attribute.parentGroup = this;

		if (indexMap) {
			const newIndex = indexMap[attribute.id];
			const index = attributes.findIndex(({ id }) => newIndex < indexMap[id]);

			if (index >= 0) {
				attributes.splice(index, 0, attribute);
				return;
			}
		}

		attributes.push(attribute);
		return attribute;
	};

	/** edit adhoc group */
	@action editAdhocGroup = (): void => {
		this.store.editingGroup = this;
	};

	/** event handler to select all attributes in the group */
	@action onAddGroup = (): void => {
		this.unselectedAttributes.forEach(this.store.addAvailableAttribute);
	};

	/** remove attribute from group */
	@action removeAttribute = (attribute: CustomViewAttribute, removeGroupIfEmpty?: boolean): void => {
		const { attributes, isAdhocGroup, isAttributeGroup, removeGroup } = this;
		const attributeToRemove = attributes.find(({ id }) => id === attribute.id);
		attributes.remove(attributeToRemove);
		if (!isAdhocGroup && !isAttributeGroup && removeGroupIfEmpty && !attributes.length) {
			removeGroup();
		}
	};

	/** remove attribute from group at index */
	@action removeAttributeAtIndex = (index: number): void => {
		this.attributes.splice(index, 1);
	};

	/** prepare attribute for addition */
	@action prepareAttributeForAddition(attribute: CustomViewAttribute): CustomViewAttribute {
		const newAttribute = attribute.copy();
		newAttribute.parentGroup = this;
		newAttribute.selected = true;
		return newAttribute;
	}

	/** add attribute to start index */
	@action addAttributeAtStart = (attribute: CustomViewAttribute): CustomViewAttribute => {
		const newAttribute = this.prepareAttributeForAddition(attribute);
		this.attributes.splice(0, 0, newAttribute);
		return newAttribute;
	};

	/** add attribute according to source attribute and target index to move to */
	@action addAttributeAtIndex = (attribute: CustomViewAttribute, sourceIndex: number, targetIndex: number, isSameGroup?: boolean): CustomViewAttribute => {
		const newAttribute = this.prepareAttributeForAddition(attribute);
		if (isSameGroup && sourceIndex < targetIndex) {
			targetIndex--;
		}
		this.attributes.splice(targetIndex, 0, newAttribute);
		return newAttribute;
	};

	/** add attribute to end index */
	@action addAttributeAtEnd = (attribute: CustomViewAttribute): CustomViewAttribute => {
		const newAttribute = this.prepareAttributeForAddition(attribute);
		this.attributes.push(newAttribute);
		return newAttribute;
	};

	/** remove attribute by key, returning true if found */
	@action removeAttributeKey = (attributeKey: C8AttributeKey, removeGroupIfEmpty?: boolean): boolean => {
		const { attributes } = this;
		const attribute = attributes.find(({ id }) => id === attributeKey);
		if (attribute) {
			this.removeAttribute(attribute, removeGroupIfEmpty);
			return true;
		}
		return false;
	};

	/** set name */
	@action setName = (name: string): void => {
		this.name = name;
	};

	/** remove selected group if empty and not the default group */
	@action removeGroup = (ev?: SyntheticEvent): void => {
		const { store } = this;
		const { joinFlatGroupsAtIndex, removeSelectedAttribute, selections } = store;
		const indexOfGroup = selections.indexOf(this);

		// remove all attributes from the selections container
		const attributesToRemove = selections.flatMap((container) => 
			container.attributes.filter((attribute) => attribute.parentGroup === this));

		attributesToRemove.forEach((attribute) => {
			removeSelectedAttribute(attribute);
		});

		selections.remove(this);
		joinFlatGroupsAtIndex(indexOfGroup);
	};

	constructor(
		store: CustomViewAttributesStore,
		id: string,
		attributes: CustomViewAttribute[] = [],
		name: string = "",
		selected = false,
		spec?: Spec,
	) {
		makeObservable(this);
		this.id = id;
		this.name = name;
		this.selected = selected;
		this.spec = spec;
		this.store = store;

		attributes.forEach((attribute) => {
			this.addAttribute(attribute);
		});
	}
}

export default CustomViewAttributeGroup;
